import React, { useCallback, useMemo, useState } from 'react';

import { link } from '@aline/routes';
import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { capitalCase } from 'change-case';
import clsx from 'clsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLoading } from 'src/AppLoading';
import { AlineButton, AlineTooltip, PermissionRoleMenu, StorableIcon } from 'src/components';
import { AlineLogo } from 'src/components/icons/AlineLogo';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

/** Type for access request from the query */
type AccessRequestInfo = NonNullable<
  NonNullable<Apollo.GetAccessRequestsByBatchIdQuery['accessRequestsByBatchId']>['accessRequests']
>[number];

const useStyles = makeStyles()(theme => ({
  card: {
    background: theme.palette.common.white,
    borderRadius: 20,
    boxShadow: '0px 12px 30px 0px rgba(50, 74, 82, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    maxWidth: 700,
    padding: theme.spacing(4),
    width: '100%',
  },
  expiredCard: {
    alignItems: 'center',
    textAlign: 'center',
  },
  fileRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  root: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    display: 'flex',
    height: '100vh',
    justifyContent: 'center',
    padding: theme.spacing(2),
    width: '100%',
  },
  storableCell: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
    overflow: 'hidden',
  },
  successCard: {
    alignItems: 'center',
    textAlign: 'center',
  },
  tableContainer: {
    maxHeight: 400,
  },
}));

type RequestGrant = {
  grantedRole: Apollo.PermissionRole;
  requestId: string;
  selected: boolean;
};

export function GrantAccessPage(): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batchId') ?? '';

  const { data, loading } = Apollo.useGetAccessRequestsByBatchIdQuery({
    skip: !batchId,
    variables: { batchId },
  });

  if (!currentUser) return <AppLoading waitingText="Getting user info..." />;
  if (loading) return <AppLoading waitingText="Loading access request..." />;

  const result = data?.accessRequestsByBatchId;

  // Show "not authorized" message if user is not the owner
  if (result?.errorReason === Apollo.AccessRequestByBatchErrorReason.NotAuthorized) {
    return (
      <div className={classes.root}>
        <Paper className={clsx(classes.card, classes.expiredCard)}>
          <AlineLogo />
          <Typography variant="h1">Not Authorized</Typography>
          <Typography>
            {
              "You don't have permission to grant access for this request. Only the file owner can approve access requests."
            }
          </Typography>
          <AlineButton color="primary" href={link.DashboardIndex()} variant="contained">
            Go to Dashboard
          </AlineButton>
        </Paper>
      </div>
    );
  }

  // Show "no ID provided" message if URL has no batch ID
  if (!batchId) {
    return (
      <div className={classes.root}>
        <Paper className={clsx(classes.card, classes.expiredCard)}>
          <AlineLogo />
          <Typography variant="h1">Missing Request ID</Typography>
          <Typography>
            No access request ID was provided. Please use the link from the access request email.
          </Typography>
          <AlineButton color="primary" href={link.DashboardIndex()} variant="contained">
            Go to Dashboard
          </AlineButton>
        </Paper>
      </div>
    );
  }

  // Show "not found" message if request doesn't exist
  if (
    result?.errorReason === Apollo.AccessRequestByBatchErrorReason.NotFound ||
    !result?.accessRequests?.length
  ) {
    return (
      <div className={classes.root}>
        <Paper className={clsx(classes.card, classes.expiredCard)}>
          <AlineLogo />
          <Typography variant="h1">Request Not Found</Typography>
          <Typography>
            This access request could not be found. It may have been deleted or the link is invalid.
          </Typography>
          <AlineButton color="primary" href={link.DashboardIndex()} variant="contained">
            Go to Dashboard
          </AlineButton>
        </Paper>
      </div>
    );
  }

  const { accessRequests } = result;
  // All requests in a batch share the same requester, message, and requestedForEmails.
  // We extract these shared fields from the first request; individual documents are
  // iterated in the table below.
  const requester = accessRequests[0].requester;
  const requesterMessage = accessRequests[0].message;
  const requestedForEmails = accessRequests[0].requestedForEmails;

  return (
    <div className={classes.root}>
      <GrantAccessForm
        accessRequests={accessRequests}
        requestedForEmails={requestedForEmails ?? undefined}
        requesterEmail={requester.email}
        requesterMessage={requesterMessage ?? undefined}
        requesterName={requester.displayName ?? undefined}
      />
    </div>
  );
}

interface GrantAccessFormProps {
  accessRequests: AccessRequestInfo[];
  requesterEmail: string;
  requestedForEmails?: string[];
  requesterMessage?: string;
  requesterName?: string;
}

function GrantAccessForm({
  accessRequests,
  requestedForEmails,
  requesterEmail,
  requesterMessage,
  requesterName,
}: GrantAccessFormProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useNotificationDispatch();

  // Filter to only pending requests (those that can still be granted)
  const pendingRequests = useMemo(
    () => accessRequests.filter(r => r.status === Apollo.AccessRequestStatus.Pending),
    [accessRequests]
  );

  const [submitted, setSubmitted] = useState(false);
  const [grants, setGrants] = useState<RequestGrant[]>(() =>
    pendingRequests.map(r => ({
      grantedRole: r.requestedRole ?? Apollo.PermissionRole.Viewer,
      requestId: r.id,
      selected: true,
    }))
  );
  const [grantAccess, { loading }] = Apollo.useGrantAccessByRequestMutation();

  const handleToggle = useCallback((requestId: string) => {
    setGrants(prev =>
      prev.map(g => (g.requestId === requestId ? { ...g, selected: !g.selected } : g))
    );
  }, []);

  const handleRoleChange = useCallback((requestId: string, role: Apollo.PermissionRole) => {
    setGrants(prev => prev.map(g => (g.requestId === requestId ? { ...g, grantedRole: role } : g)));
  }, []);

  const handleSubmit = async () => {
    const selectedGrants = grants.filter(g => g.selected);
    if (selectedGrants.length === 0) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Please select at least one file to grant access.',
          variant: 'warning',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    const result = await grantAccess({
      variables: {
        input: {
          grants: selectedGrants.map(g => ({
            grantedRole: g.grantedRole,
            requestId: g.requestId,
          })),
        },
      },
    });
    if (!result.error) setSubmitted(true);
  };

  if (submitted) {
    return (
      <Paper className={clsx(classes.card, classes.successCard)}>
        <AlineLogo />
        <Typography variant="h1">Access Granted</Typography>
        <Typography>
          {requestedForEmails?.length
            ? `${requestedForEmails.join(', ')} now ${requestedForEmails.length > 1 ? 'have' : 'has'} access to the selected files.`
            : `${requesterName || requesterEmail} now has access to the selected files.`}
        </Typography>
        <AlineButton
          color="primary"
          variant="contained"
          onClick={() => navigate(link.DashboardIndex())}
        >
          Go to Dashboard
        </AlineButton>
      </Paper>
    );
  }

  // If all requests have been processed, show "already processed" message
  if (pendingRequests.length === 0) {
    return (
      <Paper className={clsx(classes.card, classes.expiredCard)}>
        <AlineLogo />
        <Typography variant="h1">Request Already Processed</Typography>
        <Typography>
          All files in this access request have already been accepted or rejected.
        </Typography>
        <AlineButton
          color="primary"
          variant="contained"
          onClick={() => navigate(link.DashboardIndex())}
        >
          Go to Dashboard
        </AlineButton>
      </Paper>
    );
  }

  const selectedCount = grants.filter(g => g.selected).length;

  return (
    <Paper className={classes.card}>
      <div className={classes.header}>
        <AlineLogo />
      </div>
      <Box>
        <Typography variant="h1">Grant Access Request</Typography>
        <Typography color="textSecondary">
          {requestedForEmails?.length
            ? `${requesterName || requesterEmail} is asking you to share ${pendingRequests.length} file${pendingRequests.length > 1 ? 's' : ''} with ${requestedForEmails.join(', ')}. Select which files to share and the permission level for each.`
            : `${requesterName || requesterEmail} requested access to ${pendingRequests.length} file${pendingRequests.length > 1 ? 's' : ''}. Select which files to share and the permission level for each.`}
        </Typography>
        {requesterMessage && (
          <Box
            sx={{
              backgroundColor: 'grey.100',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
              mt: 2,
              p: 2,
            }}
          >
            <Typography color="textSecondary" variant="body2">
              Message from requester:
            </Typography>
            <Typography sx={{ fontStyle: 'italic', mt: 0.5 }}>{`"${requesterMessage}"`}</Typography>
          </Box>
        )}
      </Box>
      <TableContainer className={classes.tableContainer} component={Paper} variant="outlined">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedCount === pendingRequests.length}
                  indeterminate={selectedCount > 0 && selectedCount < pendingRequests.length}
                  onChange={() => {
                    const allSelected = selectedCount === pendingRequests.length;
                    setGrants(prev => prev.map(g => ({ ...g, selected: !allSelected })));
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 600 }}>File</Typography>
              </TableCell>
              <TableCell width={150}>
                <Typography sx={{ fontWeight: 600 }}>Permission</Typography>
              </TableCell>
              <TableCell width={100}>
                <Typography sx={{ fontWeight: 600 }}>Status</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Show pending requests first (selectable) */}
            {pendingRequests.map(request => {
              const grant = grants.find(g => g.requestId === request.id);
              return (
                <TableRow
                  key={request.id}
                  hover
                  className={classes.fileRow}
                  onClick={() => handleToggle(request.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={grant?.selected ?? false} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 350 }}>
                    <div className={classes.storableCell}>
                      {request.document && (
                        <StorableIcon
                          iconProps={{ height: 20, width: 20 }}
                          storable={request.document}
                        />
                      )}
                      <AlineTooltip
                        arrow
                        placement="top"
                        title={request.document?.name || 'Untitled Document'}
                      >
                        <Typography noWrap>
                          {request.document?.name || 'Untitled Document'}
                        </Typography>
                      </AlineTooltip>
                    </div>
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <PermissionRoleMenu
                      disabled={!grant?.selected}
                      handleSelect={role => handleRoleChange(request.id, role)}
                      value={grant?.grantedRole ?? Apollo.PermissionRole.Viewer}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label="Pending" size="small" variant="outlined" />
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Show already processed requests (not selectable) */}
            {accessRequests
              .filter(r => r.status !== Apollo.AccessRequestStatus.Pending)
              .map(request => {
                return (
                  <TableRow key={request.id} className={classes.fileRow} sx={{ opacity: 0.6 }}>
                    <TableCell padding="checkbox">
                      <Checkbox disabled checked={false} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 350 }}>
                      <div className={classes.storableCell}>
                        {request.document && (
                          <StorableIcon
                            iconProps={{ height: 20, width: 20 }}
                            storable={request.document}
                          />
                        )}
                        <AlineTooltip
                          arrow
                          placement="top"
                          title={request.document?.name || 'Untitled Document'}
                        >
                          <Typography noWrap>
                            {request.document?.name || 'Untitled Document'}
                          </Typography>
                        </AlineTooltip>
                      </div>
                    </TableCell>
                    <TableCell>
                      {request.grantedRole && (
                        <Typography color="textSecondary" variant="body2">
                          {capitalCase(request.grantedRole)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={capitalCase(request.status)} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <AlineButton href={link.DashboardIndex()} variant="outlined">
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={loading || selectedCount === 0}
          loading={loading}
          variant="contained"
          onClick={handleSubmit}
        >
          Grant Access
        </AlineButton>
      </Box>
    </Paper>
  );
}
